import type { GuideSection } from './types';

export const GUIDE_SECTIONS: readonly GuideSection[] = [
  {
    id: 'getting-started',
    title: 'Primeros pasos',
    icon: 'Pulse',
    blurb: 'Lo esencial para empezar a usar la plataforma en 10 minutos.',
    articles: [
      {
        id: 'intro',
        title: 'Qué es EquipoMag Bookings',
        time: '2 min',
        excerpt: 'Plataforma multi-centro para gestionar reservas, agenda y clientes desde un único panel.',
        body: [
          { type: 'p', text: 'EquipoMag Bookings es una plataforma de reservas pensada para negocios con uno o varios centros físicos. Las reservas entran desde un widget público embebible y se gestionan desde este panel.' },
          { type: 'h', text: 'Quién usa qué' },
          { type: 'list', items: [
            'Tus clientes finales reservan desde el widget público (no acceden a este panel).',
            'El staff del centro entra al panel para confirmar, mover o cancelar reservas, y para ver su agenda.',
            'El center admin hace lo mismo más la configuración del centro: horarios, servicios, profesionales.',
            'El superadmin puede crear y editar centros, asignar horarios entre centros, ver informes globales y administrar permisos.',
          ]},
          { type: 'h', text: 'Vocabulario rápido' },
          { type: 'dl', items: [
            ['Reserva', 'Una cita entre un cliente y un profesional en un centro y momento concretos.'],
            ['Estado', 'Etapa del ciclo de vida de una reserva (Pendiente, Confirmada, Cancelada, etc).'],
            ['Centro', 'Una ubicación física con su propia agenda, profesionales y servicios.'],
            ['Servicio', 'Un tratamiento o sesión que un cliente puede reservar (duración + precio opcional).'],
            ['Sala', 'Recurso físico opcional que puede asociarse a un servicio o staff.'],
            ['Widget', 'El componente embebible en tu web que ven los clientes.'],
            ['Magic link', 'Enlace de un solo uso enviado por email para acceder sin contraseña.'],
            ['Slot override', 'Bloqueo puntual de una franja del calendario (festivo, formación, mantenimiento).'],
          ]},
        ],
      },
      {
        id: 'first-login',
        title: 'Primer acceso al panel',
        time: '3 min',
        excerpt: 'Cómo entrar sin contraseña y dónde encontrar cada cosa.',
        body: [
          { type: 'h', text: 'Acceder por primera vez' },
          { type: 'ol', items: [
            'El superadmin que te invita te da de alta con tu email.',
            'Recibirás un email con un enlace de acceso (magic link). No hay contraseñas.',
            'Pulsa el enlace para entrar; caduca a los 30 minutos.',
            'Para volver a entrar, ve a /admin/login, escribe tu email y recibirás un nuevo enlace.',
          ]},
          { type: 'h', text: 'Recorrido por la interfaz' },
          { type: 'p', text: 'El panel se divide en cinco secciones verticales en la barra lateral: Resumen, Operación, Catálogo, Analítica y Plataforma. Por defecto entras a Inicio (center admin) o a Centros (superadmin).' },
          { type: 'tip', text: 'En mobile la barra lateral desaparece y aparece un menú hamburguesa arriba a la izquierda + una barra de pestañas inferior con los atajos más usados.' },
        ],
      },
      {
        id: 'roles',
        title: 'Roles y permisos',
        time: '2 min',
        excerpt: 'Diferencias entre Staff, Center admin y Superadmin.',
        body: [
          { type: 'p', text: 'Cada usuario tiene un único rol que determina qué puede ver y hacer. Los permisos están acotados al centro asignado, salvo para Superadmin.' },
          { type: 'table',
            headers: ['Rol', 'Reservas', 'Clientes', 'Servicios', 'Staff', 'Centros', 'Configuración', 'Horarios'],
            rows: [
              ['Staff', 'Su centro', 'Lectura', 'Lectura', '—', '—', '—', '—'],
              ['Center admin', 'Su centro', 'Su centro', 'Su centro', 'Su centro', '—', 'Su centro', '—'],
              ['Superadmin', '—', '—', '—', '—', 'Todos', '—', 'Todos'],
            ],
          },
          { type: 'tip', text: 'El rol se asigna al invitar a un usuario desde Staff → Invitar profesional. Solo superadmin puede crear nuevos centros.' },
        ],
      },
    ],
  },
  {
    id: 'bookings',
    title: 'Gestión de reservas',
    icon: 'Book',
    blurb: 'Crear, mover, cancelar y aprobar citas. Estados y ciclo de vida.',
    articles: [
      {
        id: 'lifecycle',
        title: 'Estados y ciclo de vida',
        time: '4 min',
        excerpt: 'Las 9 etapas por las que pasa una reserva, desde solicitud hasta completada.',
        body: [
          { type: 'p', text: 'Cada reserva sigue un flujo de estados que queda registrado en su historial. Estos son los estados posibles, en orden típico:' },
          { type: 'statelist', states: [
            { code: 'PENDING',                  tone: 'warn',    desc: 'Email enviado al cliente, esperando que verifique la reserva.' },
            { code: 'PENDING_CLINIC_APPROVAL',  tone: 'warn',    desc: 'Cliente verificó la reserva; esperando aprobación del centro (modo manual).' },
            { code: 'CONFIRMED',                tone: 'ok',      desc: 'Reserva confirmada. Aparece en agenda y se envían recordatorios automáticos.' },
            { code: 'CHECKED_IN',               tone: 'info',    desc: 'Cliente ha llegado y está en sesión.' },
            { code: 'COMPLETED',                tone: 'neutral', desc: 'Sesión terminada con éxito.' },
            { code: 'RESCHEDULED',              tone: 'info',    desc: 'Reserva movida a otra fecha/hora; se mantiene el historial.' },
            { code: 'CANCELLED',                tone: 'danger',  desc: 'Cancelada por cliente o staff antes de la hora.' },
            { code: 'NOSHOW',                   tone: 'danger',  desc: 'Cliente no se presentó. Lo marca automáticamente el cron tras la franja de gracia.' },
            { code: 'REJECTED',                 tone: 'danger',  desc: 'Solo en modo manual: el centro rechazó la solicitud.' },
          ]},
          { type: 'tip', text: 'Si tu centro está en modo automático (Configuración → Modo de confirmación), las reservas saltan directamente a CONFIRMED al verificar el email. En modo manual se quedan en PENDING_CLINIC_APPROVAL hasta que tú decides.' },
        ],
      },
      {
        id: 'create',
        title: 'Crear una reserva manualmente',
        time: '2 min',
        excerpt: 'Cuando un cliente llama o pasa por el centro y quieres reservar tú directamente.',
        body: [
          { type: 'ol', items: [
            'Desde Reservas, pulsa "+ Nueva reserva" (próximamente — de momento usa el widget público desde "Ver widget de reservas" en Configuración).',
            'Busca al cliente por email o nombre, o créalo nuevo si no existe.',
            'Elige el servicio y selecciona profesional, fecha y franja horaria.',
            'Revisa el resumen y confirma. La reserva se crea directamente como CONFIRMED.',
          ]},
          { type: 'tip', text: 'Las reservas creadas por staff no necesitan verificación de email. El cliente recibe directamente el email con el .ics adjunto.' },
        ],
      },
      {
        id: 'approval',
        title: 'Aprobar o rechazar solicitudes',
        time: '2 min',
        excerpt: 'Cuando el centro está en modo manual, las reservas pasan por tu mano antes de confirmarse.',
        body: [
          { type: 'p', text: 'En modo manual, las reservas verificadas por el cliente quedan en PENDING_CLINIC_APPROVAL hasta que un center admin o superadmin las aprueba o rechaza.' },
          { type: 'h', text: 'Desde Inicio' },
          { type: 'list', items: [
            'En la pantalla Inicio verás una tarjeta "Cola de aprobaciones" cuando haya solicitudes pendientes.',
            'Pulsa cualquier fila para abrir el drawer del booking.',
            'Botones Aprobar / Rechazar / Cancelar en la cabecera del drawer.',
          ]},
          { type: 'h', text: 'Desde Reservas' },
          { type: 'list', items: [
            'Filtra por estado "Pend. aprobación" en el desplegable superior.',
            'Click en una fila → drawer con las mismas acciones.',
          ]},
          { type: 'tip', text: 'Al rechazar, el cliente recibe un email automático. Al aprobar, recibe el email de confirmación + el .ics.' },
        ],
      },
      {
        id: 'reschedule',
        title: 'Reprogramar una reserva',
        time: '2 min',
        excerpt: 'Mover una cita a otra fecha u hora.',
        body: [
          { type: 'ol', items: [
            'Abre la reserva (drawer desde Reservas, Calendario o Inicio).',
            'Pulsa "Reprogramar" — se abre un selector con los slots libres del próximo periodo.',
            'Elige nuevo slot y confirma. El cliente recibe un email con la nueva fecha/hora.',
          ]},
          { type: 'tip', text: 'El historial queda en el drawer: verás "Confirmada → Reprogramada → Confirmada" con los timestamps.' },
        ],
      },
    ],
  },
  {
    id: 'schedule',
    title: 'Calendario y horarios',
    icon: 'Calendar',
    blurb: 'Vistas del calendario, slot overrides y horarios del personal.',
    articles: [
      {
        id: 'calendar-views',
        title: '4 vistas del Calendario',
        time: '2 min',
        excerpt: 'Día, Semana, Mes y Agenda — cuándo usar cada una.',
        body: [
          { type: 'dl', items: [
            ['Día',     'Una sola columna con horas 08:00–21:00. Útil cuando quieres ver con detalle un día concreto.'],
            ['Semana',  'Vista por defecto. 7 columnas Lun–Dom con grid horario. Bueno para planificar la semana entera.'],
            ['Mes',     'Grid 7×6 con cards pequeños. Cada día con bookings muestra el número total y una barra verde. Click en un día → te lleva a la vista Día.'],
            ['Agenda',  'Lista vertical de los próximos 14 días agrupada por día. Bueno para repasar lo que viene sin scrollear horarios.'],
          ]},
          { type: 'tip', text: 'En mobile la vista Agenda es la más cómoda; las demás requieren scroll horizontal.' },
        ],
      },
      {
        id: 'business-hours',
        title: 'Horario de apertura del centro',
        time: '2 min',
        excerpt: 'Define los días y franjas en los que el centro está abierto.',
        body: [
          { type: 'p', text: 'El horario de apertura del centro determina los slots ofrecidos en el widget público. Se configura desde Configuración → Horario de apertura.' },
          { type: 'list', items: [
            'Activa o desactiva el horario semanal con el toggle superior.',
            'Marca cada día como "Cerrado" o define hora de apertura y cierre.',
            'Los cambios afectan inmediatamente al widget (con cache de slot listing de unos segundos).',
          ]},
          { type: 'tip', text: 'Para bloqueos puntuales (festivos, formación, reuniones) usa "Bloquear horario" desde Calendario → Día/Semana en vez de editar el horario semanal.' },
        ],
      },
      {
        id: 'slot-overrides',
        title: 'Bloquear franjas concretas',
        time: '2 min',
        excerpt: 'Cierres temporales sin tocar el horario semanal.',
        body: [
          { type: 'ol', items: [
            'En Calendario, vista Día o Semana, pulsa "Bloquear horario".',
            'Elige fecha, hora desde, hora hasta y un motivo opcional.',
            'El bloqueo aparece en rojo en el calendario y los slots ocupados desaparecen del widget público.',
            'Para eliminarlo, pulsa la ✕ del bloque en el calendario.',
          ]},
          { type: 'tip', text: 'Si bloqueas una franja con reservas activas existentes, aparece un aviso ámbar arriba para que las revises o canceles manualmente.' },
        ],
      },
      {
        id: 'staff-schedule',
        title: 'Horarios del personal (superadmin)',
        time: '3 min',
        excerpt: 'Asigna turnos staff×día×semana desde la página Horarios.',
        body: [
          { type: 'p', text: 'La página /admin/horarios (superadmin only) permite asignar a cada profesional su franja por día en cada semana.' },
          { type: 'h', text: 'Cómo funciona' },
          { type: 'list', items: [
            'Click en cualquier celda staff×día → modal con 5 franjas predefinidas (Mañana, Tarde, Día completo, Tarde-noche, Mañana corta).',
            'Selecciona una franja para asignarla, o "Dejar libre" para borrar.',
            'Stats arriba: total de horas semanales, turnos libres, distribución por centro.',
            '"Copiar sem. anterior" duplica los turnos de la semana pasada como punto de partida.',
            'Botón "Exportar" descarga la semana en CSV.',
          ]},
          { type: 'tip', text: 'Actualmente los horarios asignados aquí son informativos: NO afectan a los slots ofrecidos en el widget. Esa integración llegará en un PR futuro.' },
        ],
      },
    ],
  },
  {
    id: 'centers',
    title: 'Centros, servicios y staff',
    icon: 'Storefront',
    blurb: 'Crear y configurar centros, gestionar el catálogo y dar de alta personal.',
    articles: [
      {
        id: 'new-center',
        title: 'Crear un nuevo centro (superadmin)',
        time: '3 min',
        excerpt: 'Alta completa con slug, timezone y allowed origins.',
        body: [
          { type: 'ol', items: [
            'Desde /admin/centers, pulsa "+ Nuevo centro".',
            'Rellena nombre, slug (la URL del widget será /widget/<slug>), zona horaria y dominios permitidos (orígenes CORS para embeber el widget).',
            'Indica el email del administrador del centro: se le crea como center_admin y recibe un email con un enlace de acceso (magic link).',
            'El nuevo center_admin entra pulsando el enlace del email — sin contraseña — y empieza a configurar.',
          ]},
          { type: 'tip', text: 'El slug debe ser único y URL-friendly (minúsculas, guiones). No se puede cambiar después sin romper el widget.' },
        ],
      },
      {
        id: 'services-mgmt',
        title: 'Gestionar servicios',
        time: '2 min',
        excerpt: 'Catálogo GLOBAL de tratamientos compartido por todos los centros (post-0028).',
        body: [
          { type: 'list', items: [
            'Desde /admin/services (solo superadmin), pulsa "+ Nuevo servicio" para crear.',
            'Define nombre y duración en minutos (los slots del widget se calculan a partir de esto + slotStepMinutes del centro).',
            'Toggle de activo: desactivar un servicio lo oculta del widget pero conserva las reservas pasadas.',
            'Asigna profesionales en el modal — sin staff marcado, el servicio NO aparece en ningún widget.',
            'Asigna salas (opt-in) sólo si quieres modelar un cuello de botella físico (p.ej. 1 sala con ducha de masaje). Si no marcas ninguna, cualquier sala libre del centro vale.',
          ]},
        ],
      },
      {
        id: 'staff-invite',
        title: 'Dar de alta un profesional',
        time: '2 min',
        excerpt: 'Crear staff con o sin acceso al panel.',
        body: [
          { type: 'ol', items: [
            'Desde /admin/staff, pulsa "+ Invitar profesional".',
            'Rellena nombre y, opcionalmente, email + rol (staff o center_admin) para que tenga login.',
            'Si no le das email, queda como recurso reservable sin acceso al panel.',
            'Al guardar con email, se le envía automáticamente un enlace de acceso (magic link). No se usan contraseñas.',
          ]},
          { type: 'tip', text: 'Para que un staff vuelva a entrar, basta con que pida un nuevo enlace desde /admin/login con su email. No hay contraseñas que resetear.' },
        ],
      },
    ],
  },
  {
    id: 'platform',
    title: 'Plataforma',
    icon: 'Heart',
    blurb: 'Salud del sistema, integraciones y cómo pedir ayuda.',
    articles: [
      {
        id: 'system-health',
        title: 'Salud del sistema (próximamente)',
        time: '1 min',
        excerpt: 'Dashboard con cron jobs, backups y errores recientes.',
        body: [
          { type: 'p', text: '/admin/sistema (superadmin) mostrará el estado de los 5 cron jobs (dead-mans-switch, purge expired, backups), los últimos backups D1 → R2 con verificación SHA-256 y emails enviados en las últimas 24h.' },
          { type: 'tip', text: 'Sección aún en construcción. El backend de salud está expuesto en /api/admin/system/health pero la UI llega en PR7.' },
        ],
      },
      {
        id: 'support',
        title: 'Pedir soporte',
        time: '1 min',
        excerpt: 'Cómo contactar al equipo técnico cuando algo no funciona.',
        body: [
          { type: 'p', text: 'Para errores, dudas o nuevas peticiones de feature, escribe a soporte@equipomag.com indicando el centro afectado y la hora aproximada del problema. Adjunta capturas si puedes.' },
          { type: 'tip', text: 'Si la página entera no carga, prueba primero a recargar con Ctrl+Shift+R. Si persiste, comprueba el dashboard de Cloudflare para incidencias activas antes de escribir.' },
        ],
      },
    ],
  },
];
